<template>
  <div class="main-content-container overflow-hidden">
    <PageTitle :pageTitle="isEditing ? 'Edit Testimonial' : 'Add Testimonial'" subTitle="Management" />

    <div class="card bg-white border-0 rounded-3 mb-4">
      <div class="card-body p-4">
        <form @submit.prevent="submitForm">
          <div class="row">
            <div class="col-lg-6 col-md-6 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Author Name</label>
              <input type="text" class="form-control" v-model="form.name" placeholder="Enter name (e.g. John Doe)" required @input="autoInitials" />
            </div>
            
            <div class="col-lg-6 col-md-6 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Role / Affiliation</label>
              <input type="text" class="form-control" v-model="form.role" placeholder="Enter role (e.g. Student at UI)" required />
            </div>

            <div class="col-lg-4 col-md-4 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Initials</label>
              <input type="text" class="form-control" v-model="form.initials" placeholder="JD" max="10" required />
            </div>

            <div class="col-lg-4 col-md-4 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Avatar Color</label>
              <div class="d-flex gap-2 align-items-center">
                <input type="color" class="form-control form-control-color" v-model="form.avatar_color" required />
                <div class="d-flex gap-1">
                  <button type="button" v-for="color in presetColors" :key="color" class="btn p-0 border wh-20 rounded-circle" :style="{ backgroundColor: color }" @click="form.avatar_color = color"></button>
                </div>
              </div>
            </div>

            <div class="col-lg-4 col-md-4 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Rating (1-5 Stars)</label>
              <select class="form-select" v-model="form.rating" required>
                <option value="1">1 Star</option>
                <option value="2">2 Stars</option>
                <option value="3">3 Stars</option>
                <option value="4">4 Stars</option>
                <option value="5">5 Stars</option>
              </select>
            </div>

            <div class="col-lg-6 col-md-6 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Bootstrap Column Class</label>
              <select class="form-select" v-model="form.col_class">
                <option value="col-lg-4 col-md-12">Three per row (col-lg-4 col-md-12)</option>
                <option value="col-lg-6 col-md-12">Two per row (col-lg-6 col-md-12)</option>
                <option value="col-lg-12">One per row (col-lg-12)</option>
              </select>
            </div>

            <div class="col-lg-6 col-md-6 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Visibility Status</label>
              <select class="form-select" v-model="form.status">
                <option :value="false">Hidden (Pending)</option>
                <option :value="true">Published (Approved)</option>
              </select>
            </div>

            <div class="col-lg-6 col-md-6 mb-4" v-if="form.order_id">
              <label class="form-label text-dark fs-14 fw-medium">Linked Order ID</label>
              <input type="text" class="form-control" :value="form.order_id" disabled />
            </div>

            <div class="col-lg-12 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Testimonial Text</label>
              <textarea class="form-control" v-model="form.text" rows="4" placeholder="Enter testimonial content..." required></textarea>
            </div>
          </div>
          
          <div class="d-flex justify-content-end gap-2">
            <RouterLink to="/testimonials" class="btn btn-secondary fs-15 fw-medium">Cancel</RouterLink>
            <button type="submit" class="btn btn-primary fs-15 fw-medium" :disabled="loading">
              <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              {{ isEditing ? 'Update Testimonial' : 'Create Testimonial' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import PageTitle from '@/components/Common/PageTitle.vue';
import testimonialService from '@/services/testimonialService';

export default defineComponent({
  name: 'TestimonialFormPage',
  components: {
    PageTitle
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    
    const isEditing = ref(false);
    const testimonialId = ref<number | null>(null);
    const loading = ref(false);

    const presetColors = ['#5f35f5', '#ff5733', '#33a1ff', '#2ebd4b', '#e63772'];
    
    const form = ref({
      name: '',
      role: '',
      initials: '',
      avatar_color: '#5f35f5',
      rating: 5,
      col_class: 'col-lg-4 col-md-12',
      text: '',
      status: false,
      order_id: null as number | null
    });

    onMounted(async () => {
      if (route.params.id) {
        isEditing.value = true;
        testimonialId.value = Number(route.params.id);
        await fetchTestimonial(testimonialId.value);
      }
    });

    const autoInitials = () => {
      if (isEditing.value || !form.value.name) return;
      const parts = form.value.name.trim().split(/\s+/);
      let init = parts[0] ? parts[0].charAt(0).toUpperCase() : '';
      if (parts[1]) {
        init += parts[1].charAt(0).toUpperCase();
      }
      form.value.initials = init.substring(0, 10);
    };

    const fetchTestimonial = async (id: number) => {
      try {
        const response = await testimonialService.getById(id);
        const data = response.data;
        form.value = {
          name: data.name,
          role: data.role,
          initials: data.initials,
          avatar_color: data.avatar_color,
          rating: data.rating,
          col_class: data.col_class || 'col-lg-4 col-md-12',
          text: data.text,
          status: Boolean(data.status),
          order_id: data.order_id || null
        };
      } catch (error) {
        console.error('Error fetching testimonial:', error);
        router.push('/testimonials');
      }
    };

    const submitForm = async () => {
      loading.value = true;
      try {
        const payload = {
          ...form.value,
          rating: Number(form.value.rating)
        };

        if (isEditing.value && testimonialId.value) {
          await testimonialService.update(testimonialId.value, payload);
        } else {
          await testimonialService.create(payload);
        }
        router.push('/testimonials');
      } catch (error: any) {
        console.error('Error saving testimonial:', error);
        if (error.response?.data?.message) {
          alert(error.response.data.message);
        } else {
          alert('Failed to save testimonial.');
        }
      } finally {
        loading.value = false;
      }
    };

    return {
      isEditing,
      form,
      loading,
      presetColors,
      submitForm,
      autoInitials
    };
  }
});
</script>

<style scoped>
.wh-20 {
  width: 20px;
  height: 20px;
}
</style>
